import { type Category, type DailyMinsAndSold } from '@gozoki/api-types';
import { Button, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { DailySalesGridRow } from './DailySalesGridRow';

interface DailySalesGridProps {
    storeId: number;
    dailyMinsAndSold: DailyMinsAndSold;
    products: { categories: Category[]; label: string; reference: string }[];
    refetch: () => void;
    autoCalculate: boolean;
    endAutoCalculate: () => void;
    calculateMinimumStock: (reference: string) => number[];
}

const columns = [
    'Produit',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
    'Dimanche',
];

export const DailySalesGrid = ({
    dailyMinsAndSold,
    products,
    storeId,
    refetch,
    autoCalculate,
    calculateMinimumStock,
    endAutoCalculate,
}: DailySalesGridProps) => {
    const [editCells, setEditCells] = useState<string[]>([]);
    const [cellsToSync, setCellsToSync] = useState<string[]>([]);

    const handleModify = useCallback((reference: string) => {
        setEditCells((prevEditCells) => [...prevEditCells, reference]);
    }, []);

    const handleSave = useCallback(
        (reference: string) => {
            setEditCells((prevEditCells) => prevEditCells.filter((c) => c !== reference));
            setCellsToSync((prevCellsToSync) => prevCellsToSync.filter((c) => c !== reference));
            refetch();
        },
        [refetch]
    );

    const handleCancel = useCallback(
        (reference: string) => {
            setEditCells(editCells.filter((c) => c !== reference));
        },
        [editCells]
    );

    const handleSaveAll = useCallback(() => {
        setCellsToSync(editCells);
        endAutoCalculate();
    }, [editCells, endAutoCalculate]);

    const handleCancelAll = useCallback(() => {
        setEditCells([]);
        setCellsToSync([]);
        endAutoCalculate();
    }, [endAutoCalculate]);

    const getDailyAverage = useCallback(
        (reference: string, day: number) => {
            if (!dailyMinsAndSold[reference.toString()]) {
                return 0;
            }
            if (!dailyMinsAndSold[reference.toString()].soldMap[day]) {
                return 0;
            }

            if (dailyMinsAndSold[reference.toString()].soldMap[day].totalDays === undefined) {
                return 0;
            }
            if (dailyMinsAndSold[reference.toString()].soldMap[day].totalCount === undefined) {
                return 0;
            }
            return (
                Math.round(
                    (100 * dailyMinsAndSold[reference.toString()].soldMap[day].totalCount) /
                        dailyMinsAndSold[reference.toString()].soldMap[day].totalDays
                ) / 100
            );
        },
        [dailyMinsAndSold]
    );

    const rows = useMemo(() => {
        const productsFiltered = products.filter(
            (product) => !!dailyMinsAndSold[product.reference]
        );
        return productsFiltered.map(({ label, reference }) => {
            const { minStock } = dailyMinsAndSold[reference];
            const { weekEndMinimumStock } = dailyMinsAndSold[reference];
            const minStockValues = {
                minimumStock: minStock || 0,
                weekEndMinimumStock: weekEndMinimumStock || 0,
                mondayMinimumStock: dailyMinsAndSold[reference].minMap.monday,
                tuesdayMinimumStock: dailyMinsAndSold[reference].minMap.tuesday,
                wednesdayMinimumStock: dailyMinsAndSold[reference].minMap.wednesday,
                thursdayMinimumStock: dailyMinsAndSold[reference].minMap.thursday,
                fridayMinimumStock: dailyMinsAndSold[reference].minMap.friday,
                saturdayMinimumStock: dailyMinsAndSold[reference].minMap.saturday,
                sundayMinimumStock: dailyMinsAndSold[reference].minMap.sunday,
            };

            const avgValues: number[] = [0, 1, 2, 3, 4, 5, 6].map((day) =>
                getDailyAverage(reference, day)
            );

            return { minStockValues, avgValues, label, reference };
        });
    }, [products, dailyMinsAndSold, getDailyAverage]);

    return (
        <Stack gap={1} p={1}>
            <Stack flexDirection="row" gap={1} borderBottom="1px solid lightGray" pb={2}>
                {columns.map((column, index) => {
                    return (
                        <Typography
                            key={`column-${column}-${
                                // biome-ignore lint/suspicious/noArrayIndexKey: index is used as a key
                                index
                            }`}
                            flex={column === 'Produit' ? 2 : 1}
                            px={column === 'Produit' ? 1 : 0}
                        >
                            {column}
                        </Typography>
                    );
                })}

                <Stack flex={2} alignItems="center">
                    {editCells.length > 0 ? (
                        <Stack flexDirection="row" gap={1}>
                            <Button onClick={handleSaveAll} variant="contained">
                                Enregistrer tout
                            </Button>
                            <Button onClick={handleCancelAll} variant="contained" color="error">
                                Annuler tout
                            </Button>
                        </Stack>
                    ) : null}
                </Stack>
            </Stack>
            {rows.map(({ avgValues, minStockValues, label, reference }) => (
                <DailySalesGridRow
                    key={`row-${reference}`}
                    storeId={storeId}
                    productReference={reference}
                    avgValues={avgValues}
                    minStockValues={minStockValues}
                    label={label}
                    edit={editCells.includes(reference)}
                    disabled={false}
                    autoCalculate={autoCalculate}
                    onModify={handleModify}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    calculateMinimumStock={calculateMinimumStock}
                    needSync={cellsToSync.includes(reference)}
                />
            ))}
        </Stack>
    );
};
